import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { fileUrl } from "../lib/api";

const VARIANTS = {
  pop:       { initial: { scale: 0.2, opacity: 0 }, animate: { scale: [0.2, 1.25, 0.95, 1], opacity: 1 }, transition: { duration: 0.9, ease: "easeOut" } },
  spin:      { initial: { rotateY: -180, scale: 0.4, opacity: 0 }, animate: { rotateY: [-180, 20, 0], scale: [0.4, 1.1, 1], opacity: 1 }, transition: { duration: 1.2, ease: "easeOut" } },
  float:     { initial: { y: 120, opacity: 0 }, animate: { y: [120, -10, 0], opacity: 1 }, transition: { duration: 1.4, ease: "easeOut" } },
  burst:     { initial: { scale: 0, opacity: 0 }, animate: { scale: [0, 1.4, 1], opacity: 1 }, transition: { duration: 0.8, ease: "easeOut" } },
  shimmer:   { initial: { opacity: 0, scale: 0.8 }, animate: { opacity: 1, scale: 1 }, transition: { duration: 0.6 } },
  rain:      { initial: { y: -140, opacity: 0 }, animate: { y: [-140, 0, 12, 0], opacity: 1 }, transition: { duration: 1.1, ease: "easeOut" } },
  heartbeat: { initial: { scale: 0.9, opacity: 0 }, animate: { scale: [0.9, 1.15, 1, 1.15, 1], opacity: 1 }, transition: { duration: 1.4, ease: "easeInOut" } },
  slide:     { initial: { x: -260, opacity: 0, rotate: -12 }, animate: { x: [-260, 20, 0], opacity: 1, rotate: 0 }, transition: { duration: 0.9, ease: "easeOut" } },
};

/**
 * CustomGiftPreview — animated render of a user-designed gift
 * ({emoji, text, colors, animation, image}). Replays on `replayKey` change.
 */
export default function CustomGiftPreview({ gift, size = 220, replayKey = 0, className = "" }) {
  const [tick, setTick] = useState(0);
  useEffect(() => { setTick((t) => t + 1); }, [replayKey, gift?.animation]);
  if (!gift) return null;
  const colors = gift.colors || { bg1: "#009B3A", bg2: "#FED100", text: "#0A0A0A" };
  const v = VARIANTS[gift.animation] || VARIANTS.pop;
  const img = fileUrl(gift.image_url || gift.image_path);
  const rain = gift.animation === "rain";
  const burst = gift.animation === "burst";
  const shimmer = gift.animation === "shimmer";
  const particles = Array.from({ length: rain ? 10 : burst ? 12 : 0 });

  return (
    <div
      className={`relative overflow-hidden rounded-3xl flex items-center justify-center ${className}`}
      style={{ width: size, height: size, background: `linear-gradient(135deg, ${colors.bg1}, ${colors.bg2})`, perspective: 800 }}
      data-testid="custom-gift-preview"
    >
      {shimmer && (
        <motion.div
          key={`sh-${tick}`}
          initial={{ x: "-120%" }}
          animate={{ x: "160%" }}
          transition={{ duration: 1.3, ease: "easeInOut" }}
          className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/60 to-transparent pointer-events-none"
        />
      )}
      {particles.map((_, i) => {
        const angle = (i / particles.length) * Math.PI * 2;
        return (
          <motion.span
            key={`p-${tick}-${i}`}
            initial={rain ? { y: -size, x: (i - particles.length / 2) * (size / particles.length), opacity: 0 } : { x: 0, y: 0, scale: 0, opacity: 0 }}
            animate={rain ? { y: size, opacity: [0, 1, 1, 0] } : { x: Math.cos(angle) * size * 0.42, y: Math.sin(angle) * size * 0.42, scale: 1, opacity: [0, 1, 0] }}
            transition={{ duration: rain ? 1.6 : 1.1, delay: i * (rain ? 0.12 : 0.02), ease: "easeOut" }}
            className="absolute pointer-events-none"
            style={{ fontSize: size * 0.11 }}
          >
            {gift.emoji || "🎁"}
          </motion.span>
        );
      })}
      <AnimatePresence mode="wait">
        <motion.div
          key={`m-${tick}`}
          initial={v.initial}
          animate={v.animate}
          transition={v.transition}
          className="flex flex-col items-center justify-center gap-1 px-3"
          style={{ transformStyle: "preserve-3d" }}
        >
          {img ? (
            <img src={img} alt="" className="object-contain drop-shadow-2xl" style={{ width: size * 0.5, height: size * 0.5 }} />
          ) : (
            <span className="drop-shadow-2xl leading-none" style={{ fontSize: size * 0.42 }}>{gift.emoji || "🎁"}</span>
          )}
          {gift.text ? (
            <span
              className="font-black tracking-tight text-center uppercase leading-tight drop-shadow"
              style={{ color: colors.text, fontSize: Math.max(11, size * 0.085) }}
              data-testid="custom-gift-text"
            >
              {gift.text}
            </span>
          ) : null}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
